import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { DocumentService } from '../services/DocumentService';
import AuthPromptModal from '../components/AuthPromptModal';

interface AuthPromptContextType {
  shouldShowPrompt: boolean;
  dismissPrompt: () => void;
  resetPrompt: () => void;
}

const AuthPromptContext = createContext<AuthPromptContextType | undefined>(undefined);

export const useAuthPrompt = () => {
  const context = useContext(AuthPromptContext);
  if (!context) {
    throw new Error('useAuthPrompt must be used within AuthPromptProvider');
  }
  return context;
};

interface AuthPromptProviderProps {
  children: ReactNode;
}

export const AuthPromptProvider: React.FC<AuthPromptProviderProps> = ({ children }) => {
  const [showModal, setShowModal] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    const checkAuthPrompt = async () => {
      // Only check once per session
      if (hasChecked) return;
      
      const isAuth = await DocumentService.isAuthenticated();
      
      // Only show modal if:
      // 1. User is not authenticated
      // 2. Has created 3+ documents
      // 3. Haven't shown it yet this session
      if (!isAuth && DocumentService.shouldPromptAuth()) {
        setShowModal(true);
      }
      
      setHasChecked(true);
    };
    
    // Initial check after a small delay to let the app load
    const timer = setTimeout(checkAuthPrompt, 1000);
    
    return () => clearTimeout(timer);
  }, [hasChecked]);

  const dismissPrompt = () => {
    setShowModal(false);
    DocumentService.markAuthPrompted(); // Mark as shown in localStorage
  };

  const resetPrompt = () => {
    setShowModal(false);
    setHasChecked(false);
  };

  const handleSuccess = () => {
    setShowModal(false);
    window.location.reload(); // Reload to update auth state
  };

  return (
    <AuthPromptContext.Provider 
      value={{ 
        shouldShowPrompt: showModal, 
        dismissPrompt,
        resetPrompt 
      }}
    >
      {children}
      <AuthPromptModal
        isOpen={showModal}
        onClose={dismissPrompt}
        onSuccess={handleSuccess}
      />
    </AuthPromptContext.Provider>
  );
};